/**
 * Hook for managing user preferences with server sync
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { preferencesApi } from '../services/preferences.service';
import { usePreferencesStore } from '../store/preferencesStore';
import { useNotificationStore } from '../store/notificationStore';
import { UserPreferences } from '../types';

export function usePreferences() {
  const queryClient = useQueryClient();
  const localPreferences = usePreferencesStore();
  const showNotification = useNotificationStore((state) => state.showNotification);

  // Fetch preferences from server
  const { data: serverPreferences, isLoading } = useQuery({
    queryKey: ['preferences'],
    queryFn: preferencesApi.get,
    staleTime: Infinity, // Preferences rarely change
    retry: false,
  });

  // Sync server preferences to local store on load
  useEffect(() => {
    if (serverPreferences) {
      localPreferences.updatePreferences(serverPreferences);
    }
  }, [serverPreferences]);

  // Mutation to save preferences
  const saveMutation = useMutation({
    mutationFn: (updates: Partial<UserPreferences>) => 
      preferencesApi.update(updates),
    onSuccess: (data) => {
      // Update local store
      localPreferences.updatePreferences(data);
      
      // Update cache
      queryClient.setQueryData(['preferences'], data);
      
      showNotification('success', 'Preferences saved successfully');
    },
    onError: (error: any) => {
      showNotification(
        'error',
        error?.response?.data?.error || 'Failed to save preferences'
      );
    },
  });

  return {
    preferences: localPreferences,
    isLoading,
    save: saveMutation.mutate,
    isSaving: saveMutation.isPending,
  };
}
